<template>
  <span v-if="d" class="csb" :class="['csb-' + d.level, 'csb-' + size]" v-tip="tip">
    <span class="csb-icon" aria-hidden="true">{{ d.level === 5 ? '👑' : '🔥' }}</span>
    <b class="csb-n">{{ d.n }}</b>
    <span class="csb-lbl">{{ size === 'lg' ? (d.n === 1 ? 'defense' : 'defenses') : 'streak' }}</span>
    <span v-if="size === 'lg' && d.word" class="csb-word">{{ d.word }}</span>
  </span>
</template>

<script>
// The reigning champion's title-defense streak (2026-10-23 redesign for the refresh): a brass tag that warms with the
// tier (lib/champStreak.js, five of them): brass, amber, gold with a glow, embers, and a crowned, shimmering LEGENDARY.
// `size` sm (the bracket and team headers) / lg (the champion card, a medallion with the tier's word). The tooltip
// spells it out and says how far the next tier is.
import { streakDisplay, nextStreakTier } from '../lib/champStreak.js';

export default {
  name: 'ChampStreakBadge',
  props: { streak: { type: Number, default: 0 }, size: { type: String, default: 'sm' } },
  computed: {
    d() { return streakDisplay(this.streak); },
    tip() {
      const d = this.d, next = nextStreakTier(this.streak);
      return {
        kicker: 'Title-defense streak',
        title: d.n + ' defense' + (d.n === 1 ? '' : 's') + ' in a row',
        sub: d.word ? 'Tier: ' + d.word : null,
        lines: [
          'The reigning champion has kept the belt through ' + d.n + ' tournament' + (d.n === 1 ? '' : 's') + ' since winning it.',
          next ? next.more + ' more to reach ' + next.word + ' (' + next.at + ').' : 'The top tier. Every defense from here is history.',
        ],
      };
    },
  },
};
</script>

<style scoped>
.csb {
  display: inline-flex; align-items: center; gap: 4px; vertical-align: middle; white-space: nowrap;
  padding: 0 7px; height: 20px; border-radius: 3px; cursor: default;
  font-family: var(--cond); font-weight: 700; letter-spacing: 0.6px; text-transform: uppercase; text-shadow: none;
  color: var(--brass-hi); background: rgba(11, 15, 25, 0.82); border: 1px solid var(--brass-edge);
}
.csb-icon { font-size: 11px; }
.csb-n { font-size: 15px; line-height: 1; color: #fff3cf; }
.csb-lbl { font-size: 10.5px; letter-spacing: 1.2px; color: var(--brass); }

/* tiers: brass → amber → gold → embers → legendary */
.csb-2 { border-color: #c98a2c; background: linear-gradient(180deg, rgba(92, 52, 12, 0.9), rgba(30, 18, 8, 0.9)); }
.csb-3 { border-color: var(--brass-hi); background: linear-gradient(180deg, rgba(120, 78, 18, 0.92), rgba(40, 24, 8, 0.92)); box-shadow: 0 0 8px rgba(243, 214, 142, 0.45); }
.csb-4 { border-color: #ff9a3a; background: linear-gradient(180deg, rgba(150, 52, 10, 0.94), rgba(52, 16, 6, 0.94)); box-shadow: 0 0 10px rgba(255, 120, 30, 0.55); }
.csb-4 .csb-n { color: #ffe3a1; }
.csb-5 { position: relative; border-color: #ffe08a; overflow: hidden;
  background: linear-gradient(180deg, rgba(120, 30, 10, 0.96), rgba(40, 10, 6, 0.96)); box-shadow: 0 0 12px rgba(255, 190, 70, 0.7), inset 0 0 6px rgba(255, 210, 120, 0.35); }
.csb-5::after { content: ''; position: absolute; inset: 0; pointer-events: none;
  background: linear-gradient(110deg, transparent 30%, rgba(255, 240, 190, 0.35) 50%, transparent 70%); background-size: 250% 100%; animation: csb-shine 2.6s linear infinite; }
.csb-5 .csb-n { color: #fff; text-shadow: 0 0 6px rgba(255, 200, 80, 0.9); }
@keyframes csb-shine { from { background-position: 150% 0; } to { background-position: -100% 0; } }

/* the champion card's medallion */
.csb-lg { flex-direction: column; justify-content: center; gap: 0; height: auto; min-width: 74px; padding: 6px 10px 5px; border-radius: 5px; border-width: 2px; }
.csb-lg .csb-icon { font-size: 15px; line-height: 1.1; }
.csb-lg .csb-n { font-family: var(--display); font-size: 30px; line-height: 1; }
.csb-lg .csb-lbl { font-size: 11px; }
.csb-lg .csb-word { margin-top: 2px; font-size: 10px; letter-spacing: 1.6px; color: #ffd98a; }

@media (prefers-reduced-motion: reduce) { .csb-5::after { animation: none; } }
</style>
